"use client";

import { usePostHog } from "posthog-js/react";
import {
  ComponentPropsWithoutRef,
  FC,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  PLAN_TO_AUDIENCE_EYEBROW,
  PLAN_TO_CREDIT_COUNT,
  PLAN_TO_EXPORT_EYEBROW,
  PLAN_TO_EXPORT_SPEC,
  PLAN_TO_VIDEO_COUNT_LIMIT,
} from "../const/planConst";
import {
  CLIPS_PER_MONTH,
  MONTHLY_PRICE,
  PublicPlanKey,
} from "../const/publicPricing";
import {
  calculateDiscountedPrice,
  usePricingExperiment,
} from "../hooks/usePricingExperiment";
import { PRO_MONTHLY_PRICE } from "../helpers/proPriceExperiment";
import { useDefaultBillingInterval } from "../hooks/useDefaultBillingInterval";
import { usePricingLimitVariant } from "../hooks/usePricingLimitVariant";
import { useProPriceVariant } from "../hooks/useProPriceVariant";
import { useProfile } from "../hooks/useProfile";
import { SubscriptionPlan } from "../hooks/usePlanCheckout";
import { DISCORD_LINK } from "./Nav";
import PrepaidBlockNotice from "./PrepaidBlockNotice";
import PricingBenefitStrip from "./PricingBenefitStrip";
import PricingCard from "./PricingCard";
import PricingTabs from "./PricingTabs";
import Typography from "./Typography";

type PricingCardsProps = {} & ComponentPropsWithoutRef<"div">;

const EXPORT_USAGE_TOOLTIP =
  '<span style="color:#f8fafc">1 export = download &amp; post a clip.</span> <span style="color:#94a3b8">Re-downloads &amp; posts to more accounts are free.</span>';

const UPLOAD_MINUTES_TOOLTIP =
  '<span style="color:#f8fafc">1 minute = 1 minute of source video.</span> <span style="color:#94a3b8">A 20-min video uses 20 minutes. Clipping, downloading, and publishing are unlimited.</span>';

const UPLOAD_VIDEOS_TOOLTIP =
  '<span style="color:#f8fafc">1 upload = 1 source video (file or link).</span> <span style="color:#94a3b8">Clipping, downloading, and publishing are unlimited.</span>';

const monthlyPlanKey = (plan: "basic" | "pro" | "proPlus"): PublicPlanKey => {
  if (plan === "proPlus") return "pro-plus";
  return plan;
};

const PricingCards: FC<PricingCardsProps> = ({ ...rest }) => {
  const { data: profile } = useProfile();
  // Follows the experiment's default (which can resolve after the first
  // render) until the user picks a tab or the effect below applies their
  // current plan's interval.
  const defaultInterval = useDefaultBillingInterval();
  const [pickedInterval, setPickedInterval] = useState<
    "monthly" | "yearly" | null
  >(null);
  const selected = pickedInterval ?? defaultInterval;
  const pricingConfig = usePricingExperiment();
  const pricingLimitVariant = usePricingLimitVariant();
  const proPriceVariant = useProPriceVariant();
  const posthog = usePostHog();
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const plan = profile?.plan ?? null;
    if (typeof plan === "string" && plan.length > 0) {
      setTimeout(() => {
        setPickedInterval(plan.includes("yearly") ? "yearly" : "monthly");
      });
    }
  }, [profile?.plan]);

  useEffect(() => {
    if (!hasTrackedRef.current) {
      hasTrackedRef.current = true;
      posthog?.capture("Pricing Showed");
    }
  }, [posthog]);

  const ORIGINAL_MONTHLY_PRICES = {
    basic: MONTHLY_PRICE.basic,
    pro: PRO_MONTHLY_PRICE[proPriceVariant],
    proPlus: MONTHLY_PRICE["pro-plus"],
  };

  const getPrice = (originalMonthlyPrice: number, isYearly: boolean) => {
    if (isYearly) {
      return calculateDiscountedPrice(
        originalMonthlyPrice,
        pricingConfig.discountPercentage,
      );
    }
    return originalMonthlyPrice;
  };

  const getUsageCopy = (planKey: "basic" | "pro" | "proPlus") => {
    const plan = monthlyPlanKey(planKey);
    const audienceEyebrow =
      PLAN_TO_AUDIENCE_EYEBROW.get(plan) ?? "For individuals";

    if (pricingLimitVariant === "export-count") {
      return {
        eyebrow: PLAN_TO_EXPORT_EYEBROW.get(plan) ?? "Post 3x a week",
        specText: PLAN_TO_EXPORT_SPEC.get(plan) ?? "",
        specTooltip: EXPORT_USAGE_TOOLTIP,
      };
    }
    if (pricingLimitVariant === "credit-count") {
      return {
        eyebrow: audienceEyebrow,
        specText: `${PLAN_TO_CREDIT_COUNT.get(plan) ?? 0} minutes/mo`,
        specTooltip: UPLOAD_MINUTES_TOOLTIP,
      };
    }
    if (pricingLimitVariant === "video-count") {
      return {
        eyebrow: audienceEyebrow,
        specText: `${PLAN_TO_VIDEO_COUNT_LIMIT.get(plan) ?? 0} uploads/mo`,
        specTooltip: UPLOAD_VIDEOS_TOOLTIP,
      };
    }
    return {
      eyebrow: audienceEyebrow,
      specText: `${CLIPS_PER_MONTH[plan]} clips/mo`,
      specTooltip: undefined,
    };
  };

  const basicUsage = getUsageCopy("basic");
  const proUsage = getUsageCopy("pro");
  const proPlusUsage = getUsageCopy("proPlus");

  const planFor = (base: SubscriptionPlan): SubscriptionPlan =>
    selected === "monthly" ? base : (`${base}-yearly` as SubscriptionPlan);

  return (
    <div
      {...rest}
      className={`${rest.className ?? ""} max-w-5xl mx-auto text-center`}
    >
      <PricingTabs
        selected={selected}
        onSelect={(value) => setPickedInterval(value as "monthly" | "yearly")}
        className="mx-auto mb-8"
      />
      <PrepaidBlockNotice className="-mt-4 mb-6" />
      <div className="flex flex-col md:flex-row gap-4 justify-center md:items-stretch max-w-4xl mx-auto">
        <PricingCard
          price={getPrice(
            ORIGINAL_MONTHLY_PRICES.basic,
            selected === "yearly",
          )}
          eyebrow={basicUsage.eyebrow}
          specText={basicUsage.specText}
          specTooltip={basicUsage.specTooltip}
          features={[]}
          plan={planFor("basic")}
          experimentVariant={pricingConfig.variant}
        />
        <PricingCard
          price={getPrice(ORIGINAL_MONTHLY_PRICES.pro, selected === "yearly")}
          eyebrow={proUsage.eyebrow}
          specText={proUsage.specText}
          specTooltip={proUsage.specTooltip}
          features={[]}
          plan={planFor("pro")}
          experimentVariant={pricingConfig.variant}
        />
        <PricingCard
          price={getPrice(
            ORIGINAL_MONTHLY_PRICES.proPlus,
            selected === "yearly",
          )}
          eyebrow={proPlusUsage.eyebrow}
          specText={proPlusUsage.specText}
          specTooltip={proPlusUsage.specTooltip}
          features={[]}
          plan={planFor("pro-plus")}
          experimentVariant={pricingConfig.variant}
        />
      </div>
      <PricingBenefitStrip className="mt-6 mb-12" />
      <Typography className="text-slate-700" variant="caption">
        Need more?{" "}
        <a className="underline text-slate-500" href={DISCORD_LINK}>
          {`Let's talk!`}
        </a>
      </Typography>
    </div>
  );
};

export default PricingCards;
